import type { PianoChart } from '../pianoChart.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, parseVariantSlug } from '../gameTypes.js';
import { ON_AND_ON_PIANO_VARIANTS } from './01-on-and-on.js';
import { SKY_HIGH_PIANO_VARIANTS } from './02-sky-high.js';
import { HEROES_PIANO_VARIANTS } from './03-heroes.js';
import { CRADLES_PIANO_VARIANTS } from './04-cradles.js';
import { MY_HEART_PIANO_VARIANTS } from './05-my-heart.js';
import { INVINCIBLE_PIANO_VARIANTS } from './06-invincible.js';
import { HOPE_PIANO_VARIANTS } from './07-hope.js';
import { ADVENTURE_PIANO_VARIANTS } from './08-adventure.js';
import { BLANK_PIANO_VARIANTS } from './09-blank.js';
import { DIAMOND_PIANO_VARIANTS } from './10-diamond.js';
import { HOLLAH_PIANO_VARIANTS } from './11-hollah.js';
import { NEKOZILLA_PIANO_VARIANTS } from './12-nekozilla.js';

/**
 * One playable Piano map: a single (track, variant) pair. Mirrors the
 * Pulse and Circles {@link import('../builtinPulseMaps/index.js').BuiltinMap}
 * shape but typed to {@link PianoChart} so consumers (the seeder, the
 * home page, the play page) get the narrowed mode-specific shape
 * directly.
 *
 * Ships all twelve NCS tracks across Normal / Hard / Extreme via the
 * onset-driven synthesiser in {@link ./pianoFromOnsets.ts}. Each track
 * file documents its own upgrade path to a curated piano-arrangement
 * MIDI when one becomes available.
 */
export interface PianoBuiltinMap {
  chart: PianoChart;
  expectedReplay: null;
}

export interface PianoBuiltinTrack {
  baseSlug: string;
  variants: Record<Variant, PianoBuiltinMap>;
}

const VARIANT_BUNDLES: ReadonlyArray<{
  baseSlug: string;
  charts: Record<Variant, PianoChart>;
}> = [
  // Order matches the Pulse / Circles catalogues so home-page tier
  // labels line up across modes.
  { baseSlug: 'on-and-on', charts: ON_AND_ON_PIANO_VARIANTS },
  { baseSlug: 'sky-high', charts: SKY_HIGH_PIANO_VARIANTS },
  { baseSlug: 'heroes', charts: HEROES_PIANO_VARIANTS },
  { baseSlug: 'cradles', charts: CRADLES_PIANO_VARIANTS },
  { baseSlug: 'my-heart', charts: MY_HEART_PIANO_VARIANTS },
  { baseSlug: 'invincible', charts: INVINCIBLE_PIANO_VARIANTS },
  { baseSlug: 'hope', charts: HOPE_PIANO_VARIANTS },
  { baseSlug: 'adventure', charts: ADVENTURE_PIANO_VARIANTS },
  { baseSlug: 'blank', charts: BLANK_PIANO_VARIANTS },
  { baseSlug: 'diamond', charts: DIAMOND_PIANO_VARIANTS },
  { baseSlug: 'hollah', charts: HOLLAH_PIANO_VARIANTS },
  { baseSlug: 'nekozilla', charts: NEKOZILLA_PIANO_VARIANTS },
];

export const BUILTIN_PIANO_TRACKS: PianoBuiltinTrack[] = VARIANT_BUNDLES.map(
  (bundle) => ({
    baseSlug: bundle.baseSlug,
    variants: Object.fromEntries(
      VARIANTS.map((v) => [
        v,
        {
          chart: bundle.charts[v],
          expectedReplay: null,
        } satisfies PianoBuiltinMap,
      ]),
    ) as Record<Variant, PianoBuiltinMap>,
  }),
);

export const BUILTIN_PIANO_MAPS: PianoBuiltinMap[] = BUILTIN_PIANO_TRACKS.flatMap(
  (track) => VARIANTS.map((v) => track.variants[v]),
);

/**
 * Look up a Piano builtin by slug (`'cradles'`, `'cradles-hard'`,
 * `'cradles-extreme'`). Mirrors the Circles + Pulse resolvers and
 * applies the same literal-then-variant resolution order so a
 * hypothetical track whose slug ends in `-hard` won't be misparsed.
 */
export function getBuiltinPianoMapBySlug(slug: string): PianoBuiltinMap | undefined {
  const literalTrack = BUILTIN_PIANO_TRACKS.find((t) => t.baseSlug === slug);
  if (literalTrack) {
    return literalTrack.variants.normal;
  }
  const { baseSlug, variant } = parseVariantSlug(slug);
  const track = BUILTIN_PIANO_TRACKS.find((t) => t.baseSlug === baseSlug);
  if (!track) {
    return undefined;
  }
  return track.variants[variant];
}

export function listBuiltinPianoTracks(): PianoBuiltinTrack[] {
  return BUILTIN_PIANO_TRACKS;
}

export function listBuiltinPianoMaps(): PianoBuiltinMap[] {
  return BUILTIN_PIANO_MAPS;
}
